import React from 'react';
import { Pressable } from 'react-native';
import { Image } from 'expo-image';

import { Button } from 'app/shopify/components/actions/Button/Button';
import { Box, Text, useTheme } from 'app/shopify/theme';

const ICON_SUCCESS = 'https://www.figma.com/api/mcp/asset/bb2014bd-900f-4b3a-93ad-33c368f718a0';
const ICON_NEUTRAL = 'https://www.figma.com/api/mcp/asset/322b3263-310c-4151-a6c1-d56731af4692';
const ICON_WARNING = 'https://www.figma.com/api/mcp/asset/52d905c2-a666-4263-9881-f3ad6b4e6b75';
const ICON_ERROR = 'https://www.figma.com/api/mcp/asset/ee18b005-2053-4fd6-905d-1515760539de';
const ICON_DISMISS = 'https://www.figma.com/api/mcp/asset/6602b393-e7d7-4afe-8c72-e648abd8f222';

export type ToastValidation = 'Success' | 'Warning' | 'Error' | 'Neutral';
export type ToastBreakpoint = 'Desktop/Tablet' | 'Mobile';

export interface ToastProps {
  label: string;
  validation?: ToastValidation;
  actions?: boolean;
  dismissButton?: boolean;
  leadingIcon?: boolean;
  breakpoint?: ToastBreakpoint;
  actionLabel?: string;
  onActionPress?: () => void;
  onDismiss?: () => void;
  /**
   * Slot optionnel pour remplacer l'etiquette du bouton d'action.
   * D'apres la construction Figma: uniquement utilisé en cas `Mobile` + `Neutral`.
   */
  children?: React.ReactNode | null;
}

function getIconByValidation(validation: ToastValidation) {
  switch (validation) {
    case 'Success':
      return ICON_SUCCESS;
    case 'Neutral':
      return ICON_NEUTRAL;
    case 'Warning':
      return ICON_WARNING;
    case 'Error':
      return ICON_ERROR;
  }
}

export function Toast({
  label,
  validation = 'Neutral',
  actions = false,
  dismissButton = true,
  leadingIcon = true,
  breakpoint = 'Desktop/Tablet',
  actionLabel = 'Undo',
  onActionPress,
  onDismiss,
  children,
}: ToastProps) {
  const theme = useTheme();
  const isMobile = breakpoint === 'Mobile';

  const backgroundColorByValidation: Record<ToastValidation, keyof typeof theme.colors> = {
    Success: 'surface.success.default',
    Neutral: 'surface.strong-neutral',
    Warning: 'surface.warning.default',
    Error: 'surface.error.default',
  };

  const rightTextColor = theme.colors['text.inverse.primary'];

  const labelWrapperStyle = isMobile ? { flex: 1, minWidth: 1 } : undefined;

  const shadowRgba = theme.colors['shadow.strong'];
  const shadowColor = /^#[\da-f]{6}$/i.test(shadowRgba) ? `${shadowRgba}29` : shadowRgba;

  /** Figma: bouton contour blanc sur surface; `tertiary` + `inverted` = bordure + texte inverse. */
  const resolvedActionLabel =
    isMobile &&
    validation === 'Neutral' &&
    children !== null &&
    (typeof children === 'string' || typeof children === 'number')
      ? String(children)
      : actionLabel;

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      padding="spacing.general.x3"
      height={56}
      minHeight={56}
      borderRadius="radius.md"
      style={{
        backgroundColor: theme.colors[backgroundColorByValidation[validation]],
        maxWidth: isMobile ? 480 : undefined,
        width: isMobile ? 352 : undefined,
        boxShadow: `0px 2px 7px ${shadowColor}`,
      }}
    >
      {leadingIcon ? (
        <Box width={20} height={20} alignItems="center" justifyContent="center">
          <Image source={{ uri: getIconByValidation(validation) }} style={{ width: 20, height: 20 }} />
        </Box>
      ) : null}

      <Box flexDirection="row" alignItems="center" paddingHorizontal="spacing.general.x2" style={labelWrapperStyle}>
        <Text variant="bodySmallMedium" style={{ color: rightTextColor }} numberOfLines={1}>
          {label}
        </Text>
      </Box>

      <Box flexDirection="row" alignItems="center" gap="spacing.general.x2">
        {actions ? (
          <Button label={resolvedActionLabel} variant="tertiary" inverted size="small" onPress={onActionPress} />
        ) : null}

        {dismissButton ? (
          <Box
            borderLeftWidth={1}
            borderLeftColor="border.inverse.divider"
            paddingLeft="spacing.general.x2"
            alignItems="center"
            justifyContent="center"
          >
            <Pressable onPress={onDismiss} disabled={!onDismiss}>
              <Box width={32} height={32} borderRadius="radius.full" alignItems="center" justifyContent="center">
                <Image source={{ uri: ICON_DISMISS }} style={{ width: 20, height: 20 }} />
              </Box>
            </Pressable>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
